/**
 * Chat Store (Pinia) - 军师对话页共享状态.
 *
 * 设计:
 *   - roles 缓存: ChatHome 拉取后缓存, Conversation 页用 role_id 反查角色名/头像.
 *   - currentConversationId: 当前所在对话, 用于返回时高亮/恢复.
 *   - convTitleUpdates: 对话标题更新队列, SSE title 事件 -> 侧栏 watch.
 *   - streamingMessages: 流式状态 Map (连接全局化, 组件卸载不断开).
 *     key = conversationId, value = StreamingState.
 *     组件通过 computed 从 store 读, 切走再回来仍能继续显示流式打字机.
 */
import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type {
  ChatRequest,
  Message,
  MessageAttachment,
  Role,
  SSEDocumentData,
  SSEDoneData,
  SSEErrorData,
  SSEImageData,
  SSEMusicData,
  SSETaskPendingData,
  SSETokenData,
  SSEVideoData,
  UUID,
} from '@/types/api'
import { listRoles } from '@/api/roles'
import { showNotify } from '@/stores/notify'
import { streamChat, streamOpening } from '@/api/conversations'

export interface ConvTitleUpdate {
  id: UUID
  title: string
}

/** 流式过程中的异步任务占位. */
export interface PendingTaskPlaceholder {
  task_id: string
  kind: 'image' | 'video' | 'music'
  message: string
}

/** 单个对话的流式状态. */
export interface StreamingState {
  /** 累积的流式文本. */
  text: string
  /** 是否正在生成. */
  isStreaming: boolean
  /** SSE 连接控制器 (全局, 不随组件销毁). */
  abortController: AbortController | null
  /** 流式过程中的工具调用附件. */
  pendingDocs: SSEDocumentData[]
  pendingImages: SSEImageData[]
  pendingVideos: SSEVideoData[]
  pendingMusic: SSEMusicData[]
  pendingTaskPlaceholders: PendingTaskPlaceholder[]
  /** 完成后固化的消息 (done 事件触发). */
  finalizedMessage: Message | null
  /** 错误信息. */
  error: string | null
  /** 自动取名通知 (title 事件). */
  lastTitle: string | null
  /** 回调: done 时通知组件启动异步任务轮询. */
  onDone?: (msg: Message) => void
}

/**
 * 将 pending 附件列表转为 MessageAttachment[].
 * 复刻 Conversation.vue 原有的 pendingToolAttachments 逻辑.
 */
function pendingToAttachments(state: StreamingState): MessageAttachment[] {
  const atts: MessageAttachment[] = []
  for (const d of state.pendingDocs) {
    atts.push({
      kind: 'file',
      url: d.file_url,
      mime_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      name: d.file_name,
    })
  }
  for (const img of state.pendingImages) {
    atts.push({ kind: 'image', url: img.file_url, mime_type: 'image/png', name: img.file_name })
  }
  for (const vid of state.pendingVideos) {
    atts.push({ kind: 'file', url: vid.file_url, mime_type: 'video/mp4', name: vid.file_name })
  }
  for (const mus of state.pendingMusic) {
    atts.push({ kind: 'file', url: mus.file_url, mime_type: 'audio/mpeg', name: mus.file_name })
  }
  for (const tp of state.pendingTaskPlaceholders) {
    const kind: MessageAttachment['kind'] = tp.kind === 'image' ? 'image' : 'file'
    const mime =
      tp.kind === 'image' ? 'image/png' : tp.kind === 'video' ? 'video/mp4' : 'audio/mpeg'
    atts.push({ kind, url: '', mime_type: mime, name: tp.message, task_id: tp.task_id })
  }
  return atts
}

export const useChatStore = defineStore('chat', () => {
  const roles = ref<Role[]>([])
  const rolesLoaded = ref(false)
  const currentConversationId = ref<UUID | null>(null)
  const convTitleUpdates = ref<ConvTitleUpdate[]>([])
  const newConversationId = ref<string | null>(null)
  const convRefreshTrigger = ref(0)

  // 流式状态: shallowRef + 手动触发 (Map 重新赋值), 避免深层响应影响性能
  const streamingMessages = shallowRef<Map<string, StreamingState>>(new Map())

  /**
   * 重建 streamingMessages 并浅拷贝每个 state 对象, 使引用变化.
   *
   * 背景: shallowRef 只在 .value 赋值时触发. 但 new Map(oldMap) 仅拷贝 entries,
   * state 对象引用不变. 组件中 `computed(() => store.getStreamingState(id))`
   * 返回同引用, Vue 的 Object.is 比较认为未变, 不触发下游 computed (streamingText
   * 等), 导致流式文本/最终消息不更新 (切走再回来才走 syncStreamingFromStore 的
   * messages.push 路径能看到). 浅拷贝 state 让引用变化, computed 链路恢复响应.
   */
  function touch(): void {
    const newMap = new Map<string, StreamingState>()
    for (const [k, v] of streamingMessages.value) {
      newMap.set(k, { ...v })
    }
    streamingMessages.value = newMap
  }

  /** 幂等加载角色. */
  async function ensureRoles(): Promise<void> {
    if (rolesLoaded.value) return
    roles.value = await listRoles()
    rolesLoaded.value = true
  }

  function getRole(roleId: UUID | null | undefined): Role | undefined {
    if (!roleId) return undefined
    return roles.value.find((r) => r.id === roleId)
  }

  function setCurrentConversation(id: UUID | null): void {
    currentConversationId.value = id
  }

  function notifyConvTitleUpdate(id: UUID, title: string): void {
    convTitleUpdates.value.push({ id, title })
  }

  function notifyNewConversation(id: UUID): void {
    newConversationId.value = String(id)
    convRefreshTrigger.value++
  }

  // ===== 流式连接全局化 =====

  function getStreamingState(conversationId: UUID | string): StreamingState | undefined {
    return streamingMessages.value.get(conversationId)
  }

  function getStreamingText(conversationId: UUID | string): string {
    return streamingMessages.value.get(conversationId)?.text ?? ''
  }

  function isConversationStreaming(conversationId: UUID | string): boolean {
    return streamingMessages.value.get(conversationId)?.isStreaming ?? false
  }

  /** 中止流式 (用户点停止按钮). */
  function abortStreaming(conversationId: UUID | string): void {
    const state = streamingMessages.value.get(conversationId)
    state?.abortController?.abort()
  }

  /** 清除流式状态 (流结束且组件已渲染固化后调用). */
  function clearStreaming(conversationId: UUID | string): void {
    streamingMessages.value.delete(conversationId)
    touch()
  }

  /**
   * 启动流式对话 (发送消息).
   * store 持有 SSE 连接, 组件卸载不断开, 回来仍能显示流式打字机.
   */
  async function startChatStream(
    conversationId: UUID,
    payload: ChatRequest,
    onDone?: (msg: Message) => void,
  ): Promise<void> {
    // 如果该对话已有进行中的流, 先 abort
    const existing = streamingMessages.value.get(conversationId)
    if (existing?.abortController) {
      existing.abortController.abort()
    }

    const state: StreamingState = {
      text: '',
      isStreaming: true,
      abortController: new AbortController(),
      pendingDocs: [],
      pendingImages: [],
      pendingVideos: [],
      pendingMusic: [],
      pendingTaskPlaceholders: [],
      finalizedMessage: null,
      error: null,
      lastTitle: null,
      onDone,
    }
    streamingMessages.value.set(conversationId, state)
    touch()

    await consumeSSE(conversationId, () => streamChat(conversationId, payload, state.abortController!.signal))
  }

  /**
   * 启动角色开场白流式.
   * 与 startChatStream 类似, 但不构造用户消息, prompt 由后端注入.
   */
  async function startOpeningStream(
    conversationId: UUID,
    roleId: UUID,
    onDone?: (msg: Message) => void,
  ): Promise<void> {
    const existing = streamingMessages.value.get(conversationId)
    if (existing?.abortController) {
      existing.abortController.abort()
    }

    const state: StreamingState = {
      text: '',
      isStreaming: true,
      abortController: new AbortController(),
      pendingDocs: [],
      pendingImages: [],
      pendingVideos: [],
      pendingMusic: [],
      pendingTaskPlaceholders: [],
      finalizedMessage: null,
      error: null,
      lastTitle: null,
      onDone,
    }
    streamingMessages.value.set(conversationId, state)
    touch()

    await consumeSSE(conversationId, () => streamOpening(conversationId, roleId, state.abortController!.signal))
  }

  /** 共用 SSE 消费循环 (chat / opening 复用). */
  async function consumeSSE(
    conversationId: UUID,
    makeStream: () => AsyncGenerator<{ event: string; data: Record<string, unknown> }>,
  ): Promise<void> {
    const convId = String(conversationId)
    try {
      const stream = makeStream()
      for await (const event of stream) {
        const current = streamingMessages.value.get(convId)
        if (!current) break // 被清除

        if (event.event === 'token') {
          const data = event.data as unknown as SSETokenData
          if (typeof data.delta === 'string' && data.delta.length > 0) {
            current.text += data.delta
            touch()
          }
        } else if (event.event === 'document') {
          current.pendingDocs.push(event.data as unknown as SSEDocumentData)
          touch()
        } else if (event.event === 'image') {
          current.pendingImages.push(event.data as unknown as SSEImageData)
          touch()
        } else if (event.event === 'video') {
          current.pendingVideos.push(event.data as unknown as SSEVideoData)
          touch()
        } else if (event.event === 'music') {
          current.pendingMusic.push(event.data as unknown as SSEMusicData)
          touch()
        } else if (event.event === 'task_pending') {
          const data = event.data as unknown as SSETaskPendingData
          current.pendingTaskPlaceholders.push({
            task_id: data.task_id,
            kind: data.kind,
            message: data.message,
          })
          touch()
        } else if (event.event === 'usage') {
          // token 计量暂不展示
        } else if (event.event === 'title') {
          const newTitle = String(event.data.title ?? '')
          if (newTitle) {
            current.lastTitle = newTitle
            convTitleUpdates.value.push({ id: conversationId, title: newTitle })
            touch()
          }
        } else if (event.event === 'done') {
          const data = event.data as unknown as SSEDoneData
          const msg: Message = {
            id: data.message_id || `local-assistant-${Date.now()}`,
            tenant_id: '',
            conversation_id: conversationId,
            role: 'assistant',
            content: current.text,
            parent_id: null,
            token_input: null,
            token_output: null,
            task_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attachments: pendingToAttachments(current),
          }
          current.finalizedMessage = msg
          current.isStreaming = false
          touch()
          // 通知组件: done 了, 可以启动异步任务轮询
          current.onDone?.(msg)
        } else if (event.event === 'error') {
          const data = event.data as unknown as SSEErrorData
          current.error = data.message || '生成失败'
          current.isStreaming = false
          touch()
        }
      }
    } catch (e) {
      const current = streamingMessages.value.get(convId)
      if (current) {
        const isAbort = e instanceof Error && e.name === 'AbortError'
        if (isAbort) {
          // 用户主动停止: 保留已有部分文本, 标记结束
          current.isStreaming = false
        } else {
          current.error = e instanceof Error ? e.message : '未知错误'
          current.isStreaming = false
          // 会员到期 / 余额不足: 弹窗提示 (Phase 1 提示联系管理员, Phase 2 预留去充值按钮)
          const errCode = (e as Error & { code?: string }).code
          if (errCode === 'MEMBERSHIP_EXPIRED' || errCode === 'INSUFFICIENT_BALANCE') {
            const isMembership = errCode === 'MEMBERSHIP_EXPIRED'
            showNotify({
              title: isMembership ? '会员已过期' : '余额不足',
              message: e instanceof Error ? e.message : (isMembership ? '请购买会员后继续使用' : '请充值后继续使用'),
              variant: 'amber',
              confirmText: '我知道了',
            })
          }
        }
        touch()
      }
    } finally {
      const current = streamingMessages.value.get(convId)
      if (current) {
        current.isStreaming = false
        current.abortController = null
        touch()
      }
    }
  }

  return {
    roles,
    rolesLoaded,
    currentConversationId,
    convTitleUpdates,
    newConversationId,
    convRefreshTrigger,
    streamingMessages,
    ensureRoles,
    getRole,
    setCurrentConversation,
    notifyConvTitleUpdate,
    notifyNewConversation,
    getStreamingState,
    getStreamingText,
    isConversationStreaming,
    abortStreaming,
    clearStreaming,
    startChatStream,
    startOpeningStream,
  }
})
